import React, { useContext, useEffect } from 'react'
import {
  Link,
  Text,
  Box,
  Flex,
  Hide,
  getPaletteColor
} from 'pcln-design-system'
import styled from 'styled-components'
import analytics from '@/shared-utils/analytics'
import { ANALYTICS_CATEGORY_HOME } from '@/constants/analytics'
import BootstrapDataContext from '@/context/BootstrapDataContext'
import fireRudderstackEvent from '@/shared-utils/rudder-stack'
import { RudderProductDesc } from './types'
import { getProductIconFilled } from './utils'

const NoInventoryWrapper = styled(Flex)`
  background-color: transparent;
  flex-direction: row;
  align-items: center;
`

const InventoryTextWrapper = styled(Flex)`
  flex-direction: column;
  justify-content: center;
  width: 100%;
`

const TextSpan = Text.span

export default function NoInventoryFallback({
  xSellMessaging,
  xSellTarget,
  xSellUrl,
  rudderChildProductCode,
  rudderParentProductCode,
  offerId,
  dataIsLoading
}: {
  xSellMessaging: string
  xSellTarget: 'Rental Cars' | 'Hotels' | 'Flights'
  xSellUrl: string
  rudderChildProductCode: RudderProductDesc
  rudderParentProductCode: RudderProductDesc
  offerId: string
  dataIsLoading: boolean
}) {
  const { webstats, customer } = useContext(BootstrapDataContext)

  useEffect(() => {
    if (!dataIsLoading) {
      fireRudderstackEvent({
        label: `Search ${xSellTarget}`,
        parentProduct: rudderParentProductCode,
        childProduct: rudderChildProductCode,
        source: 'homepage',
        subType: 'cross sell',
        hasInventory: false,
        cguid: webstats.clientGUID,
        emailAddress: customer.emailAddress,
        parentOfferId: offerId,
        rudderEvent: true,
        category: 'impression'
      })
    }
  }, [
    xSellTarget,
    rudderParentProductCode,
    rudderChildProductCode,
    webstats.clientGUID,
    customer.emailAddress,
    offerId,
    dataIsLoading
  ])

  const ProductIcon = getProductIconFilled(xSellTarget || 'hotel')
  return (
    <>
      <NoInventoryWrapper
        data-label={`Search ${xSellTarget}`}
        data-parent-product={rudderParentProductCode}
        data-child-product={rudderChildProductCode}
        data-source="homepage"
        data-sub-type="cross sell"
        data-has-inventory={false}
        data-cguid={webstats.clientGUID}
        data-email-address={customer.emailAddress}
        data-parent-offer-id={offerId}
        data-rudder-event
        color="background.lightest"
        borderRadius="xl"
        py={3}
        px={24}
        width={[1, 2 / 3, 8 / 11]}
        data-testid="no-inventory-fallback"
      >
        <Hide xs sm md>
          <Flex
            justifyContent="center"
            alignItems="center"
            borderRadius="full"
            mr={4}
            width="80px"
            height="80px"
            bg={getPaletteColor('background.lightest')}
          >
            <ProductIcon size="32px" />
          </Flex>
        </Hide>

        <InventoryTextWrapper>
          <Flex>
            <Hide xxl xl lg>
              <Flex
                justifyContent="center"
                alignItems="center"
                borderRadius="full"
                mr={2}
                width="56px"
                height="56px"
                bg={getPaletteColor('background.lightest')}
              >
                <ProductIcon size="32px" />
              </Flex>
            </Hide>
            <Box>
              <Box
                color="highlight.light"
                mr="auto"
                px={2}
                py={1}
                borderRadius="lg"
                mb={2}
                display="table"
              >
                <Text color="highlight.shade" fontSize={12} bold>
                  SAVE UP TO 40%
                </Text>
              </Box>
              <Text
                color="text.heading"
                textStyle={['heading5', null, 'heading3']}
                mb={3}
              >
                {xSellMessaging}
              </Text>
            </Box>
          </Flex>
          <TextSpan>
            <Link
              onClick={() => {
                analytics.fireEvent({
                  action: 'Feature Box CTA',
                  category: ANALYTICS_CATEGORY_HOME,
                  label: 'Cross Sell Search Button'
                })
              }}
              href={xSellUrl}
              target="_blank"
              data-testid="XSELL_SEARCH_BUTTON"
              variation="fill"
            >
              Search {xSellTarget}
            </Link>
          </TextSpan>
        </InventoryTextWrapper>
      </NoInventoryWrapper>
    </>
  )
}
